import { IBreadcrumbItem, IContextualMenuItem, IDropdownOption, PivotItem } from '@fluentui/react';
import {
  Button,
  buttonStylesPrimary,
  buttonStylesSplitSecondary,
  CommonStyles,
  debounce,
  Dropdown,
  getDescriptionForItemCount,
  IconButton,
  iconButtonStylesToggle,
  IconName,
  ISearchProps,
  loaderStylesSpinnerButtonSecondary,
  PageHeader,
  pageHeaderStylesSecondary,
  Pivot,
  pivotContainerStylesFill,
  pivotStylesFill,
  Search,
  useClassNames,
} from '@h2oai/ui-kit';
import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FeatureSetDraftList } from '../../../components/FeatureSetDraftList/FeatureSetDraftList';
import { FeatureSetList } from '../../../components/FeatureSetList/FeatureSetList';
import { RegisterFeatureSetPanel } from '../../../components/RegisterFeatureSetPanel/RegisterFeatureSetPanel';
import { ListableFeatureSetDraft } from '../../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import {
  FeatureSetSearchRequestFeatureSetSortField,
  SortDirection,
} from '../../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { ContentsTitle, FeatureSetAction, SortInfo } from '../../../featurestore/models';
import { featurestorePageClassName } from '../../../featurestore/styles';
import { useSearchParams } from '../../../utils/hooks';
import { featureSetsStylesDefault, IFeatureSetsClassNames, IFeatureSetsStyles } from './FeatureSets.styles';

const getBreadcrumbItems = (ownerOnly?: boolean, currentTitle = ContentsTitle.featureSetList) => {
  const secondTitle = ownerOnly ? ContentsTitle.myFeatureSets : ContentsTitle.allFeatureSets;
  return [
    { text: ContentsTitle.featureSets, key: ContentsTitle.featureSets },
    { text: secondTitle, key: secondTitle },
    {
      text: currentTitle,
      key: currentTitle,
      isCurrentItem: true,
    },
  ];
};

const sortOptions: IDropdownOption[] = [
  { key: FeatureSetSearchRequestFeatureSetSortField.FEATURE_SET_SORT_FIELD_NAME, text: 'Name' },
  { key: FeatureSetSearchRequestFeatureSetSortField.FEATURE_SET_SORT_FIELD_DESCRIPTION, text: 'Description' },
  { key: FeatureSetSearchRequestFeatureSetSortField.FEATURE_SET_SORT_FIELD_AUTHOR, text: 'Author' },
  { key: FeatureSetSearchRequestFeatureSetSortField.FEATURE_SET_SORT_FIELD_LAST_UPDATE, text: 'Last Updated' },
  { key: FeatureSetSearchRequestFeatureSetSortField.FEATURE_SET_SORT_FIELD_PROJECT_NAME, text: 'Project Name' },
];

const sortDirectionOptions: IDropdownOption[] = [
  { key: SortDirection.SORT_ASC, text: 'Asc' },
  { key: SortDirection.SORT_DESC, text: 'Desc' },
];

const getRefreshMenuItems = (
  onClick: (e?: React.MouseEvent | React.KeyboardEvent, item?: IContextualMenuItem) => void
): IContextualMenuItem[] => {
  return [
    { key: '0', text: 'Manually', onClick },
    { key: '30', text: 'Every 30s', onClick },
    { key: '60', text: 'Every 60s', onClick },
  ];
};

const defaultSortInfo = {
  field: '',
  direction: SortDirection.SORT_ASC,
};

interface IFeatureSetsProps {
  ownedOnly?: boolean;
}

export const FeatureSets = ({ ownedOnly }: IFeatureSetsProps) => {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab');
  const featureSetDraftId = params.get('featureSetDraftId');
  const [searchText, setSearchText] = useState('');
  const [sortInfo, setSortInfo] = useState<SortInfo>({ ...defaultSortInfo });
  const [refreshButtonInfo, setRefreshButtonInfo] = useState<{
    disabled: boolean;
    text: string;
    refreshTime: number;
  }>({
    disabled: false,
    text: 'Manually',
    refreshTime: 0,
  });
  const [isCardView, setIsCardView] = useState(true);
  const [description, setDescription] = useState('');
  const [selectedPivotKey, setSelectedPivotKey] = useState(tab ? tab : ContentsTitle.featureSetList);
  const [reload, setReload] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showRegisterFeatureSetPanel, setShowRegisterFeatureSetPanel] = useState(false);
  const [breadcrumbItems, setBreadcrumbItems] = useState<IBreadcrumbItem[]>(getBreadcrumbItems(ownedOnly));
  const refSelectedItem = useRef<ListableFeatureSetDraft>();
  const changeSearchText = useCallback((text: string) => {
    setSearchText(text);
  }, []);
  const onSearchTextChange = useMemo(() => debounce(300, changeSearchText), [changeSearchText]);
  const onUpdateData = useCallback((count: number, pageSize: number, pageToken: string, name: string) => {
    setDescription(getDescriptionForItemCount(count, pageSize, pageToken, name));
    setReload(false);
  }, []);
  const onRefreshMenuClick = useCallback((_e?: React.MouseEvent | React.KeyboardEvent, item?: IContextualMenuItem) => {
    if (item) {
      setRefreshButtonInfo({
        disabled: item.key !== '0',
        text: item.text || '',
        refreshTime: +item.key,
      });
    }
  }, []);
  const searchProps: ISearchProps = {
    placeholder: 'Search Feature Set by name',
    onSearchTextChange,
  };
  useEffect(() => {
    setBreadcrumbItems(getBreadcrumbItems(ownedOnly));
    params.set('tab', selectedPivotKey);
    setParams(params);
  }, [ownedOnly]);
  useEffect(() => {
    if (featureSetDraftId) {
      refSelectedItem.current = { featureSetDraftId };
      setShowRegisterFeatureSetPanel(true);
    }
  }, [featureSetDraftId]);
  const classNames = useClassNames<IFeatureSetsStyles, IFeatureSetsClassNames>(
    'FeatureSets',
    featureSetsStylesDefault,
    undefined,
    featurestorePageClassName
  );
  return (
    <div className={classNames.root} data-test="feature-sets">
      <PageHeader
        styles={pageHeaderStylesSecondary}
        style={{ marginBottom: 16 }}
        pageTitle={ownedOnly ? ContentsTitle.myFeatureSets : ContentsTitle.allFeatureSets}
        breadcrumbProps={{
          items: breadcrumbItems,
        }}
        actions={
          <Button
            styles={buttonStylesPrimary}
            text="Register Feature Set"
            onClick={() => {
              refSelectedItem.current = {};
              setShowRegisterFeatureSetPanel(true);
            }}
          />
        }
      />
      <Pivot
        styles={pivotStylesFill}
        pivotContainerStyles={pivotContainerStylesFill}
        selectedKey={selectedPivotKey}
        onLinkClick={(item?: PivotItem) => {
          const title = item?.props.itemKey as ContentsTitle;
          if (title) {
            setBreadcrumbItems(getBreadcrumbItems(ownedOnly, title));
            setSelectedPivotKey(title);
            params.set('tab', title);
            setParams(params);
          }
        }}
        items={[
          {
            key: ContentsTitle.featureSetList,
            headerText: ContentsTitle.featureSetList,
            content: (
              <div className={classNames.list} data-test="pivot-feature-set-list">
                <div className={classNames.description}>
                  <div className={classNames.descriptionText}>{description}</div>
                </div>
                <div className={classNames.search}>
                  <Search style={{ maxWidth: '611px', flex: 1 }} {...searchProps} />
                  <div className={classNames.toggleButtons}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {isCardView && (
                        <>
                          <Dropdown
                            data-test="dropdown-sort"
                            styles={{ root: { width: '150px' } }}
                            placeholder="Sort By"
                            options={sortOptions}
                            selectedKey={sortInfo?.field}
                            onChange={(_e, option) => {
                              if (option) {
                                setSortInfo({
                                  ...sortInfo,
                                  field: option.key as FeatureSetSearchRequestFeatureSetSortField,
                                });
                              }
                            }}
                          />
                          <Dropdown
                            data-test="dropdown-sort-direction"
                            placeholder="Sort Direction"
                            options={sortDirectionOptions}
                            selectedKey={sortInfo?.direction}
                            onChange={(_e, option) => {
                              if (option) {
                                setSortInfo({ ...sortInfo, direction: option.key as SortDirection });
                              }
                            }}
                          />
                        </>
                      )}
                      <div style={{ paddingTop: '6px' }}>
                        <IconButton
                          style={{ marginRight: 8 }}
                          styles={iconButtonStylesToggle}
                          iconName="ExpandMenu"
                          checked={isCardView}
                          onClick={() => {
                            setSortInfo({ ...defaultSortInfo });
                            setIsCardView(true);
                          }}
                        />
                        <IconButton
                          styles={iconButtonStylesToggle}
                          iconName="CollapseMenu"
                          checked={!isCardView}
                          onClick={() => {
                            setSortInfo({ ...defaultSortInfo });
                            setIsCardView(false);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <FeatureSetList
                  ownedOnly={ownedOnly}
                  isCardView={isCardView}
                  searchText={searchText}
                  sortInfo={sortInfo}
                  onUpdateData={onUpdateData}
                />
              </div>
            ),
          },
          {
            key: ContentsTitle.ongoingRegistration,
            headerText: ContentsTitle.ongoingRegistration,
            content: (
              <div className={classNames.list}>
                <div className={classNames.description}>
                  <div className={classNames.descriptionText}>{description}</div>
                  <div className={classNames.toggleButtons}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <div style={{ lineHeight: '32px' }}>Refresh Type: </div>
                      <Button
                        styles={buttonStylesSplitSecondary}
                        text={refreshButtonInfo.text}
                        menuIconName={IconName.ChevronDown}
                        split
                        menuItems={getRefreshMenuItems(onRefreshMenuClick)}
                        loading={loading}
                        primaryDisabled={refreshButtonInfo.disabled}
                        loaderWithMenuButton
                        loaderProps={{
                          label: 'Refreshing',
                          styles: loaderStylesSpinnerButtonSecondary,
                        }}
                        onClick={() => setReload(true)}
                      />
                      <div style={{ ...(CommonStyles.flexCenterVertically as CSSProperties) }}>
                        <div>
                          <IconButton
                            style={{ marginRight: 8 }}
                            styles={iconButtonStylesToggle}
                            iconName="ExpandMenu"
                            checked={isCardView}
                            onClick={() => setIsCardView(true)}
                          />
                          <IconButton
                            styles={iconButtonStylesToggle}
                            iconName="CollapseMenu"
                            checked={!isCardView}
                            onClick={() => setIsCardView(false)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <FeatureSetDraftList
                  isCardView={isCardView}
                  reload={reload}
                  refreshTime={refreshButtonInfo.refreshTime}
                  onUpdateData={onUpdateData}
                  onContinue={(item) => {
                    refSelectedItem.current = item;
                    setShowRegisterFeatureSetPanel(true);
                  }}
                  onLoading={(loading) => setLoading(loading)}
                />
              </div>
            ),
          },
        ]}
      />
      <RegisterFeatureSetPanel
        action={refSelectedItem.current?.featureSetDraftId ? FeatureSetAction.draft : FeatureSetAction.register}
        featureSetDraftId={refSelectedItem.current?.featureSetDraftId}
        show={showRegisterFeatureSetPanel}
        onDismiss={(updated) => {
          if (updated) {
            setSelectedPivotKey(ContentsTitle.ongoingRegistration);
            setReload(true);
          }
          refSelectedItem.current = {};
          setShowRegisterFeatureSetPanel(false);
        }}
      />
    </div>
  );
};
